import { useTranslation } from 'react-i18next';
import type { PaymentStatus } from '../types/payment';

interface PaymentStatusBadgeProps {
  readonly status: string; // string (not PaymentStatus) — tolerates unknown future values gracefully
}

// Record<PaymentStatus, string> enforces exhaustiveness at compile time — adding a new
// PaymentStatus value without updating these tables causes a TS error (Effective TypeScript §28).
const STATUS_STYLES: Record<PaymentStatus, string> = {
  SUCCEEDED: 'bg-green-100 text-green-700',
  INITIATED: 'bg-amber-100 text-amber-700',
  FAILED:    'bg-red-100 text-red-700',
  EXPIRED:   'bg-gray-100 text-gray-600',
};

const KNOWN_STATUSES = Object.keys(STATUS_STYLES) as PaymentStatus[];

export const PaymentStatusBadge = ({ status }: PaymentStatusBadgeProps) => {
  const { t } = useTranslation('badges');

  const STATUS_LABELS: Record<PaymentStatus, string> = {
    SUCCEEDED: t('statusPaid'),
    INITIATED: t('statusPending'),
    FAILED:    t('statusFailed'),
    EXPIRED:   t('statusExpired'),
  };

  const isKnown = KNOWN_STATUSES.includes(status as PaymentStatus);
  const classes = isKnown
    ? STATUS_STYLES[status as PaymentStatus]
    : 'bg-gray-100 text-gray-500';
  const label = isKnown
    ? STATUS_LABELS[status as PaymentStatus]
    : status;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${classes}`}>
      {label}
    </span>
  );
};
